import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, CreditCard, LifeBuoy, LogOut, PauseCircle } from "lucide-react";
import { useShellStore } from "../../store";
import { useTenantBranding } from "../../hooks/useTenantBranding";
import {
  formatDay,
  formatMoney,
  readOrgAccess,
  reloadIntoShell,
  useBillingStatus,
  usePayStatement,
} from "../../lib/billing";
import { CardOnFile } from "./CardOnFile";

/** Where "Contact support" goes. Per deployment; WeCo's own desk otherwise. */
export const SUPPORT_EMAIL = import.meta.env.VITE_SUPPORT_EMAIL || "support@we-co.io";

/** While a payment is being confirmed, re-read status this often… */
const CONFIRMING_POLL_MS = 5_000;
/** …for this long, then fall back to a slow poll. */
const CONFIRMING_WINDOW_MS = 3 * 60_000;
const IDLE_POLL_MS = 60_000;

/**
 * The whole app for a paused (billing-only) workspace.
 *
 * Rendered by the shell instead of the sidebar and modules — no module route
 * is reachable from here, and nothing else (call room, notifications, the
 * assistant) is mounted. An Owner or Admin sees the outstanding bills and can
 * pay them; anybody else is told their administrator can.
 *
 * After checkout Paymob sends the browser back to `/billing/return`, which
 * lands here too. The page then polls until the server says access is full
 * again, renews the session so the token stops saying `billing_only`, and
 * reloads into the full shell.
 */
export function WorkspacePausedPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const logout = useShellStore((s) => s.logout);
  const refreshAccessToken = useShellStore((s) => s.refreshAccessToken);
  const branding = useTenantBranding();
  const [confirming, setConfirming] = useState(
    () => typeof window !== "undefined" && window.location.pathname === "/billing/return",
  );
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (!confirming) return;
    const id = window.setTimeout(() => setConfirming(false), CONFIRMING_WINDOW_MS);
    return () => window.clearTimeout(id);
  }, [confirming]);

  const { data: status, isLoading, isError, refetch, dataUpdatedAt } = useBillingStatus({
    refetchInterval: confirming ? CONFIRMING_POLL_MS : IDLE_POLL_MS,
  });
  const pay = usePayStatement();

  // Paid (online or marked paid by WeCo): renew the session for a token that
  // says "full", then reload into the shell. If the token has not caught up
  // yet (People caches the state briefly), the next poll tries again.
  //
  // Not cancelled on unmount on purpose: the fresh token itself flips the gate
  // and unmounts this page, and the reload must still happen — the app should
  // start clean rather than grow the shell inside a page that was never it.
  useEffect(() => {
    if (!status || status.enforced_access !== "full") return;
    setRestoring(true);
    void (async () => {
      const ok = await refreshAccessToken();
      if (ok && readOrgAccess(useShellStore.getState().accessToken) === "full") {
        reloadIntoShell();
      }
    })();
  }, [status, dataUpdatedAt, refreshAccessToken]);

  const appName = branding.app_name || "WeCo";
  const canPay = !!status?.can_pay;
  const bills = status?.outstanding ?? [];

  return (
    <div
      className="min-h-dvh w-full flex items-start sm:items-center justify-center bg-background text-foreground p-4 sm:p-8 overflow-y-auto"
      data-testid="workspace-paused-page"
    >
      <main className="w-full max-w-xl">
        <div className="flex items-center gap-3 mb-6">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt="" className="h-9 w-9 rounded-xl object-contain" />
          ) : null}
          <span className="text-[15px] font-black tracking-tight">{appName}</span>
        </div>

        <div className="rounded-3xl border border-border bg-card p-6 sm:p-8 shadow-sm">
          {restoring ? (
            <div className="flex items-start gap-3" role="status">
              <CheckCircle2 size={22} className="shrink-0 text-emerald-600 dark:text-emerald-400 mt-0.5" aria-hidden />
              <div>
                <h1 className="text-[22px] font-black tracking-tight">{t("billing.paused.restoringTitle")}</h1>
                <p className="text-[14px] text-muted-foreground mt-2">{t("billing.paused.restoringBody")}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <PauseCircle size={24} className="shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" aria-hidden />
                <div className="min-w-0">
                  <h1 className="text-[22px] font-black tracking-tight">{t("billing.paused.title")}</h1>
                  <p className="text-[14px] text-muted-foreground mt-2 text-pretty">{t("billing.paused.body")}</p>
                  <p className="text-[14px] text-muted-foreground mt-2 text-pretty">
                    {canPay ? t("billing.paused.bodyCanPay") : t("billing.paused.bodyMember")}
                  </p>
                </div>
              </div>

              {confirming && (
                <p
                  role="status"
                  className="mt-5 text-[13px] px-3 py-2.5 rounded-2xl bg-muted/60 border border-border"
                >
                  {t("billing.paused.confirming")}
                </p>
              )}

              {canPay && (
                <section className="mt-6" aria-labelledby="paused-bills-heading">
                  <h2 id="paused-bills-heading" className="text-[12px] font-black uppercase tracking-wider text-muted-foreground mb-3">
                    {t("billing.paused.outstanding")}
                  </h2>
                  {bills.length === 0 ? (
                    <p className="text-[13px] text-muted-foreground">{t("billing.paused.noBills")}</p>
                  ) : (
                    <ul className="space-y-2">
                      {bills.map((bill) => (
                        <li
                          key={bill.id}
                          className="flex items-center gap-3 flex-wrap rounded-2xl border border-border px-4 py-3"
                          data-testid="paused-bill"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-[13.5px] font-bold truncate">{bill.plan_name}</p>
                            <p className="text-[12px] text-muted-foreground">
                              {formatDay(bill.period_start, locale)} – {formatDay(bill.period_end, locale)}
                            </p>
                          </div>
                          <span className="text-[14px] font-black">{formatMoney(bill.total, bill.currency, locale)}</span>
                          {status?.payment_enabled && (
                            <button
                              type="button"
                              onClick={() => pay.mutate(bill.id)}
                              disabled={pay.isPending}
                              className="h-9 px-4 rounded-2xl text-[13px] font-bold bg-primary text-primary-foreground hover:brightness-95 disabled:opacity-60 inline-flex items-center gap-1.5"
                            >
                              <CreditCard size={15} aria-hidden />
                              {pay.isPending && pay.variables === bill.id ? t("billing.openingCheckout") : t("billing.payNow")}
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {bills.length > 0 && !status?.payment_enabled && (
                    <p className="text-[12.5px] text-muted-foreground mt-3">{t("billing.paymentUnavailableNote")}</p>
                  )}
                </section>
              )}

              {status && <CardOnFile status={status} />}

              {isError && (
                <div className="mt-5 flex items-center gap-3 flex-wrap text-[13px]">
                  <span className="text-muted-foreground">{t("billing.paused.statusError")}</span>
                  <button type="button" onClick={() => void refetch()} className="font-bold underline">
                    {t("billing.paused.retry")}
                  </button>
                </div>
              )}
              {isLoading && <p className="mt-5 text-[13px] text-muted-foreground">{t("shell.loading")}</p>}
            </>
          )}

          <div className="mt-8 pt-5 border-t border-border flex items-center gap-2 flex-wrap">
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="h-9 px-4 rounded-2xl text-[13px] font-bold border border-border hover:bg-muted inline-flex items-center gap-1.5"
            >
              <LifeBuoy size={15} aria-hidden />
              {t("billing.paused.contactSupport")}
            </a>
            <button
              type="button"
              onClick={() => void logout()}
              className="h-9 px-4 rounded-2xl text-[13px] font-bold border border-border hover:bg-muted inline-flex items-center gap-1.5"
            >
              <LogOut size={15} aria-hidden className="rtl-flip-icon" />
              {t("billing.paused.signOut")}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * Show the billing-only page instead of the app when the workspace is paused.
 *
 * Either signal is enough: the access token's `org_access` claim (instant,
 * no request) or the server's `enforced_access` (catches a pause that landed
 * after the token was issued). Super admins are never kept out — People never
 * restricts them, and they need the console to resolve exactly this.
 */
export function BillingAccessGate({ children }: { children: React.ReactNode }) {
  const accessToken = useShellStore((s) => s.accessToken);
  const isSuperAdmin = useShellStore((s) => !!s.user?.isSuperAdmin);
  const { data: status } = useBillingStatus();
  const billingOnly =
    !isSuperAdmin &&
    (readOrgAccess(accessToken) === "billing_only" || status?.enforced_access === "billing_only");
  if (billingOnly) return <WorkspacePausedPage />;
  return <>{children}</>;
}
